import { savedOfferIds, savedOfferReturnUrl } from "./savedOfferNavigation";

/**
 * Beta 30 (#630): ein flaches Hauptmenü statt Arbeitsbereiche mit Unterleiste.
 * Bestellungen, Warenkorb und Konto stehen oben in der Kopfzeile; darunter
 * nur noch diese Seiten. Was jemand sieht, folgt aus seinen Rechten
 * (`active_membership`); jede Route behält ihre eigene Prüfung.
 */
export type NavigationAccess = {
  can_review_prices: boolean;
  can_manage_cost_centers: boolean;
  can_manage_imports: boolean;
  can_manage_purchase_selections: boolean;
};

export type NavigationPage = {
  label: string;
  to: string;
  owns?: (pathname: string, search: string) => boolean;
  /** Shown only when this decision is true; absent means everyone sees it. */
  needs?: keyof NavigationAccess;
};

const beneath = (path: string, parent: string) => path === parent || path.startsWith(`${parent}/`);

export const MAIN_MENU: readonly NavigationPage[] = [
  { label: "Startseite", to: "/", owns: path => path === "/" },
  { label: "Artikel", to: "/articles" },
  { label: "Belege", to: "/belege", needs: "can_review_prices" },
  { label: "Rechnungsprüfung", to: "/rechnungspruefung", needs: "can_review_prices" },
  { label: "Kostenstellen", to: "/cost-centers", needs: "can_manage_cost_centers", owns: path => beneath(path, "/cost-centers") || beneath(path, "/projects") },
  { label: "Analyse", to: "/reports/variance", needs: "can_review_prices" },
  { label: "Preisspiegel", to: "/preisspiegel", needs: "can_review_prices", owns: path => path === "/preisspiegel" || path === "/angebote/vergleichen" || beneath(path, "/angebote/auswahl") },
];

export function mainMenu(access?: NavigationAccess): NavigationPage[] {
  // Without an answer from the server nothing is hidden: the menu waits for the
  // profile rather than flickering a smaller one first.
  return MAIN_MENU.filter(page => !access || !page.needs || access[page.needs]);
}

export function ownsPage(page: NavigationPage, pathname: string, search = ""): boolean {
  return page.owns ? page.owns(pathname, search) : beneath(pathname, page.to.split("?")[0]);
}

export function activeMenuPage(pathname: string, search: string): NavigationPage | undefined {
  return MAIN_MENU.find(page => ownsPage(page, pathname, search));
}

export function detailLabel(pathname: string, search: string): string | null {
  const params = new URLSearchParams(search);
  // Die Artikelseite hat ihren eigenen Zurück-Link; kein zweiter Seitenpfad darüber (#630).
  if (pathname.startsWith("/articles/")) return null;
  // Every kind of document now lives under /belege/<id> (#546); the id says
  // which one it is, so the crumb keeps naming the kind.
  if (pathname.startsWith("/belege/") && pathname !== "/belege/gutschriften") {
    const id = decodeURIComponent(pathname.slice("/belege/".length));
    if (id.startsWith("angebot:")) return "Angebot";
    if (id.startsWith("einzelauftrag:")) return "Auftragsbestätigung";
    return "Belegdetails";
  }
  if (pathname.startsWith("/angebote/auswahl/")) return "Einkaufsauswahl";
  if (pathname === "/angebote/vergleichen") return "Angebotsvergleich";
  if (pathname.startsWith("/angebote/")) return "Angebot";
  if (pathname.startsWith("/auftragsbestaetigungen/") || pathname.startsWith("/orders/")) return "Auftragsdetails";
  if (pathname.startsWith("/cost-centers/")) return "Kostenstellendetails";
  if (pathname === "/imports/review") return "Daten prüfen";
  if (pathname === "/coverage" && params.get("tab") === "profiles") return "Datenquellen";
  if (pathname === "/coverage" && params.get("tab") === "coverage") return "Datenabdeckung";
  if (pathname === "/reports/variance" && params.has("case_article")) return "Artikelpreis";
  return null;
}

export function breadcrumbPageHref(page: NavigationPage, search: string, from?: unknown): string {
  if (page.to === "/preisspiegel") return savedOfferReturnUrl(savedOfferIds(new URLSearchParams(search)), new URLSearchParams(search));
  if (page.to === "/auftragsbestaetigungen" && typeof from === "string" && (from === "/auftragsbestaetigungen" || from.startsWith("/auftragsbestaetigungen?"))) return from;
  const costReturn = new URLSearchParams(search).get("return_to");
  if (page.to === "/cost-centers" && costReturn && (costReturn === "/cost-centers" || costReturn.startsWith("/cost-centers?"))) return costReturn;
  if (page.to === "/cost-centers" && typeof from === "string" && (from === "/cost-centers" || from.startsWith("/cost-centers?"))) return from;
  if (page.to === "/articles" && typeof from === "string" && (from === "/articles" || from.startsWith("/articles?"))) return from;
  if (page.to !== "/reports/variance") return page.to;
  const params = new URLSearchParams(search);
  params.delete("case_article");
  params.delete("case_supplier");
  return `${page.to}${params.size ? `?${params}` : ""}`;
}
